'use client';

import * as React from 'react';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaOptionsType } from 'embla-carousel';
import { SliderImage } from '@/hen-ui/composites/SliderImage';

export interface SliderProps extends React.PropsWithChildren {
  json?: {
    url: string;
    alt?: string;
  }[];
  width?: number;
  height?: number;
}

type SliderContentProps = React.PropsWithChildren<{
  json?: {
    url: string;
    alt?: string;
  }[];
  width?: number;
  height?: number;
}>;

function SliderContent({ children, json, width, height }: SliderContentProps) {
  const STATIC_GRID_IMGS_NUM = 2;
  const MIN_IMGS_SMOOTH_LOOP = 10;
  const AUTOPLAY_DELAY_MS = 80;
  const TRANSITION_DURATION_MS = 7000;

  const autoplay = React.useRef(
    Autoplay({
      delay: AUTOPLAY_DELAY_MS,
      stopOnInteraction: false,
      jump: false,
    }),
  );

  const [emblaRef] = useEmblaCarousel(
    {
      align: 'start',
      loop: true,
      dragFree: true,
      slidesToScroll: 'auto',
      duration: TRANSITION_DURATION_MS,
    } as EmblaOptionsType,
    [autoplay.current],
  );

  const hasJson = json && json.length > 0;

  // Duplicate items to ensure smooth scrolling
  const duplicatedItems = React.useMemo(() => {
    const items = hasJson
      ? json
      : children
        ? React.Children.toArray(children)
        : null;

    if (!items || items.length === 0) return null;

    if (
      items.length <= STATIC_GRID_IMGS_NUM ||
      items.length >= MIN_IMGS_SMOOTH_LOOP
    ) {
      return items;
    }

    const repetitions = Math.ceil(MIN_IMGS_SMOOTH_LOOP / items.length);
    return Array(repetitions).fill(items).flat();
  }, [hasJson, json, children]);

  if (!duplicatedItems) return null;

  if (hasJson) {
    return (
      <div className='relative'>
        <div ref={emblaRef} className='overflow-hidden'>
          <div className='flex gap-max mt-sm'>
            {duplicatedItems.map((image, index) => (
              <SliderImage
                key={`${image.url}-${index}`}
                height={height}
                src={image.url}
                alt={image.alt || `Grid Image ${index + 1}`}
              />
            ))}
            <div className='flex-none w-sm' aria-hidden='true' />
          </div>
        </div>
      </div>
    );
  }

  // Render children-based images
  // GridImage children have grid-specific styling (max-w/max-h constraints)
  // Override those constraints for slider by wrapping in slider-appropriate container
  return (
    <div className='relative'>
      <div ref={emblaRef} className='overflow-hidden'>
        <div className='flex gap-max mt-sm'>
          {duplicatedItems.map((child, index) => (
            <div
              key={index}
              className='flex-none [&>div]:max-w-none [&>div]:max-h-none'
            >
              {child}
            </div>
          ))}
          <div className='flex-none w-sm' aria-hidden='true' />
        </div>
      </div>
    </div>
  );
}

SliderContent.displayName = 'SliderContent';

export function Slider({ json, width, height, children }: SliderProps) {
  const hasChildren = children != null;
  const hasJson = json && json.length > 0;

  if (!hasChildren && !hasJson) {
    return null;
  }

  return (
    <SliderContent json={json} width={width} height={height}>
      {children}
    </SliderContent>
  );
}

Slider.displayName = 'Slider';
